import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, ChevronRight, Shield } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { BentoCard } from "@/components/BentoCard";
import { cn } from "@/lib/utils";
import { selectActiveProject, useProjectStore } from "@/store/projectStore";
import {
  ADDONS_ENABLED,
  allCharges,
  formatRand,
  formatSADate,
  getAddOns,
  getTier,
  getUpgradeTarget,
  useBillingStore,
  type BillingCharge,
  type ChargeStatus,
  type ProjectEntitlement,
  type Usage,
  type UsageFeature,
} from "@/store/billingStore";

const EASE = [0.22, 1, 0.36, 1] as const;

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cad">{children}</span>
);

// ── Project switcher ──
function ProjectSwitcher() {
  const projects = useProjectStore((s) => s.projects);
  const active = useProjectStore(selectActiveProject);
  const setActive = useProjectStore((s) => s.setActiveProject);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative w-full max-w-sm">
      <button
        type="button"
        data-testid="billing-project-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center justify-between rounded-xl border bg-white/[0.03] px-4 py-3 text-left backdrop-blur-xl transition-colors duration-200",
          open ? "border-safety/60" : "border-cad/30 hover:border-cad/60",
        )}
      >
        <span className="flex flex-col">
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-cad">Project</span>
          <span data-testid="billing-active-project" className="truncate font-display text-base font-semibold text-white">
            {active?.name ?? "No project"}
          </span>
        </span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="text-cad">
          <ChevronDown size={16} />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && projects.length > 0 && (
          <motion.ul
            role="listbox"
            data-testid="billing-project-list"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="absolute inset-x-0 top-[calc(100%+8px)] z-30 max-h-64 overflow-y-auto rounded-xl border border-cad/40 bg-obsidian-deep/95 p-1.5 backdrop-blur-2xl"
          >
            {projects.map((p) => (
              <li key={p.id} role="option" aria-selected={p.id === active?.id}>
                <button
                  type="button"
                  data-testid={`billing-project-option-${p.id}`}
                  onClick={() => { setActive(p.id); setOpen(false); }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3.5 py-2.5 text-left font-sans text-sm transition-colors duration-150",
                    p.id === active?.id ? "bg-safety/10 text-safety" : "text-white/80 hover:bg-white/5 hover:text-white",
                  )}
                >
                  {p.name}
                  {p.id === active?.id && <Check size={14} />}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Usage meter row ──
function UsageRow({ label, feature }: { label: string; feature: UsageFeature }) {
  if (feature === null) return null;
  const testId = `billing-usage-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
  if (feature === "coming_soon") {
    return (
      <div data-testid={testId} className="flex items-center justify-between py-3">
        <span className="font-sans text-sm text-white/80">{label}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cad">Coming soon</span>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((feature.used / feature.limit) * 100));
  return (
    <div data-testid={testId} className="flex flex-col gap-2 py-3">
      <div className="flex items-center justify-between">
        <span className="font-sans text-sm text-white/80">{label}</span>
        <span className="font-mono text-[11px] text-white/60">{feature.used} / {feature.limit}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-cad/30">
        <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: pct / 100 }} transition={{ duration: 0.9, ease: EASE }} style={{ originX: 0 }} className="h-full rounded-full bg-safety" />
      </div>
    </div>
  );
}

const TAG: Record<ChargeStatus, { label: string; className: string }> = {
  confirmed: { label: "Confirmed", className: "border-[#7e9b8e]/50 text-[#9fb8ad]" },
  pending: { label: "Pending", className: "border-cad/50 text-white/55" },
  failed: { label: "Failed", className: "border-[#b98a3d]/50 text-[#c79a52]" },
  refunded: { label: "Refunded", className: "border-cad/50 text-cad" },
};

function StatusTag({ status }: { status: ChargeStatus }) {
  const t = TAG[status];
  return (
    <span className={cn("rounded-md border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em]", t.className)}>
      {t.label}
    </span>
  );
}

function ChargeRow({ charge }: { charge: BillingCharge }) {
  return (
    <button
      type="button"
      data-testid={`billing-charge-${charge.id}`}
      className="group flex w-full items-center justify-between gap-4 py-4 text-left transition-colors duration-200 hover:bg-white/[0.02]"
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate font-sans text-sm text-white">{charge.item}</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cad">
          {formatSADate(charge.date)} · {charge.projectName}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-3">
        <span className="font-mono text-sm text-white/80">{formatRand(charge.amountZar)}</span>
        <StatusTag status={charge.status} />
        <ChevronRight size={14} className="text-cad transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-safety" />
      </span>
    </button>
  );
}

function Skeleton() {
  return (
    <div data-testid="billing-skeleton" className="flex flex-col gap-5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-32 animate-pulse rounded-2xl border border-cad/20 bg-white/[0.02]" />
      ))}
    </div>
  );
}

const USAGE_LABELS: Record<keyof Usage, string> = {
  planRuns: "Plan interpretation runs",
  quoteRuns: "Quote Analyser runs",
  chatCredits: "Chat credits",
  homeVision: "Home Vision generations",
};

export default function BillingScreen() {
  const navigate = useNavigate();
  const project = useProjectStore(selectActiveProject);
  const entitlements = useBillingStore((s) => s.entitlements);
  const chargesMap = useBillingStore((s) => s.charges);
  const ensure = useBillingStore((s) => s.ensure);
  const beginUpgrade = useBillingStore((s) => s.beginUpgrade);
  const finalizeUpgrade = useBillingStore((s) => s.finalizeUpgrade);
  const requestRefund = useBillingStore((s) => s.requestRefund);

  const [loading, setLoading] = useState(true);
  const [refundOpen, setRefundOpen] = useState(false);
  const [whyOpen, setWhyOpen] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const projects = useProjectStore((s) => s.projects);

  // Seed oldest project first (so the seeded Standard lands on the oldest, not the active one).
  useEffect(() => {
    const sorted = [...projects].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    sorted.forEach((p, i) => ensure(p.id, p.name, i === 0));
  }, [projects, ensure]);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  const ent: ProjectEntitlement | undefined = project ? entitlements[project.id] : undefined;

  // Resume a confirming payment across reloads/navigation (the simulated ITN finalises when the delay elapses).
  useEffect(() => {
    if (!project || !ent || ent.status !== "confirming" || !ent.pendingTier) return;
    const elapsed = Date.now() - (ent.confirmingSince ?? Date.now());
    const remaining = Math.max(0, 2200 - elapsed);
    const tier = ent.pendingTier;
    confirmTimer.current = setTimeout(() => finalizeUpgrade(project.id, project.name, tier), remaining);
    return () => {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
    };
  }, [project, ent, finalizeUpgrade]);

  const doUpgrade = (tierId: ProjectEntitlement["tier"]) => {
    if (!project) return;
    beginUpgrade(project.id, tierId);
  };

  const history = allCharges(chargesMap);
  const shownHistory = showAllHistory ? history : history.slice(0, 3);
  const upgradeTarget = ent ? getUpgradeTarget(ent.tier) : null;

  return (
    <AppShell>
      <section data-testid="billing-screen" className="min-h-screen bg-obsidian-deep">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-5 py-6 md:px-10 md:py-10">
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="flex flex-col gap-2">
            <Eyebrow>Account · Billing</Eyebrow>
            <h1 data-testid="billing-headline" className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">Plans &amp; billing</h1>
          </motion.div>

          {!loading && !project ? (
            <BentoCard data-testid="billing-no-project" className="flex flex-col items-start gap-4 bg-obsidian p-8">
              <p className="font-sans text-sm text-white/55">You're on the Free plan. Create a project and upgrade it when you're ready.</p>
              <button type="button" data-testid="billing-empty-add" onClick={() => navigate("/projects/new")} className="rounded-xl bg-safety px-6 py-3 font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90">+ Add a property</button>
            </BentoCard>
          ) : loading || !ent ? (
            <Skeleton />
          ) : (
            <>
              {/* 1. Project switcher */}
              <ProjectSwitcher />

              {/* Payment state banners */}
              <AnimatePresence>
                {ent.status === "confirming" && (
                  <motion.div data-testid="billing-confirming" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-3 rounded-xl border border-safety/40 bg-safety/[0.06] px-4 py-3">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-safety" />
                    <span className="font-sans text-sm text-white/80">Payment received — confirming…</span>
                  </motion.div>
                )}
                {ent.status === "failed" && (
                  <motion.div data-testid="billing-failed" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center justify-between gap-3 rounded-xl border border-[#b98a3d]/50 bg-[#b98a3d]/[0.06] px-4 py-3">
                    <span className="font-sans text-sm text-white/80">Payment didn't go through. Nothing was charged.</span>
                    <button type="button" data-testid="billing-retry" onClick={() => doUpgrade(ent.tier)} className="font-mono text-[11px] uppercase tracking-[0.16em] text-safety">Try again</button>
                  </motion.div>
                )}
                {ent.status === "refunded" && (
                  <motion.div data-testid="billing-refunded" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-xl border border-cad/40 bg-white/[0.02] px-4 py-3">
                    <span className="font-sans text-sm text-white/70">This project was refunded and is back on the Free plan.</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 2. Current plan card */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }}>
                <BentoCard data-testid="billing-current-plan" highlight={ent.tier !== "free" && ent.status === "active"} className="flex flex-col gap-4 bg-obsidian p-6 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-1.5">
                      <Eyebrow>Current plan</Eyebrow>
                      <h2 data-testid="billing-tier-name" className="font-display text-2xl font-semibold tracking-tight text-white">
                        {ent.tier === "free" ? "Free plan" : `${getTier(ent.tier).name} plan`}
                      </h2>
                      {ent.tier !== "free" ? (
                        <span className="font-mono text-sm text-white/70">{formatRand(ent.amountZar)} · once-off</span>
                      ) : (
                        <span className="font-sans text-sm text-white/55">You're on the Free plan. Upgrade a project when you're ready.</span>
                      )}
                    </div>
                    {ent.tier !== "free" && ent.status === "active" && (
                      <span data-testid="billing-active-badge" className="rounded-md border border-safety/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-safety">Active</span>
                    )}
                  </div>
                  {ent.tier !== "free" && ent.paidAt && (
                    <div className="flex flex-col gap-1 border-t border-cad/20 pt-4">
                      <span className="font-mono text-[11px] text-white/60">Paid {formatSADate(ent.paidAt)}</span>
                      <span className="font-sans text-sm text-white/55">Lifetime access to this project and future updates for your plan.</span>
                    </div>
                  )}
                </BentoCard>
              </motion.div>

              {/* 3. Plan usage */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.6, ease: EASE }}>
                <BentoCard data-testid="billing-usage" className="flex flex-col bg-obsidian p-6 md:p-8">
                  <Eyebrow>Plan usage</Eyebrow>
                  <div className="mt-2 flex flex-col divide-y divide-cad/15">
                    {(Object.keys(USAGE_LABELS) as (keyof Usage)[]).map((k) => (
                      <UsageRow key={k} label={USAGE_LABELS[k]} feature={ent.usage[k]} />
                    ))}
                  </div>
                </BentoCard>
              </motion.div>

              {/* 4. Upgrade card */}
              {upgradeTarget && ent.status !== "confirming" && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6, ease: EASE }}>
                  <BentoCard data-testid="billing-upgrade" className="flex flex-col gap-4 bg-obsidian p-6 md:p-8">
                    <div className="flex flex-col gap-1">
                      <h3 className="font-display text-lg font-semibold tracking-tight text-white">Upgrade to {upgradeTarget.name}</h3>
                      <span className="font-sans text-sm text-white/55">{formatRand(upgradeTarget.amountZar)} · once-off for this project. Lifetime access, no renewals.</span>
                    </div>
                    <button
                      type="button"
                      data-testid="billing-upgrade-btn"
                      onClick={() => doUpgrade(upgradeTarget.id)}
                      className="self-start rounded-xl bg-safety px-6 py-3 font-display text-sm font-semibold text-obsidian transition-opacity duration-200 hover:opacity-90"
                    >
                      Upgrade to {upgradeTarget.name} · {formatRand(upgradeTarget.amountZar)}
                    </button>
                  </BentoCard>
                </motion.div>
              )}

              {/* 5. Add-ons (behind feature flag) */}
              {ADDONS_ENABLED && (
                <BentoCard data-testid="billing-addons" className="flex flex-col gap-3 bg-obsidian p-6">
                  <Eyebrow>Add-ons</Eyebrow>
                  {getAddOns().map((a) => (
                    <div key={a.id} className="flex items-center justify-between py-2">
                      <span className="font-sans text-sm text-white">{a.label}</span>
                      <span className="font-mono text-sm text-white/70">{formatRand(a.amountZar)}</span>
                    </div>
                  ))}
                </BentoCard>
              )}

              {/* 6. Add another property */}
              <button
                type="button"
                data-testid="billing-add-property"
                onClick={() => navigate("/projects/new")}
                className="rounded-xl border border-cad/40 bg-white/[0.02] px-5 py-4 text-left font-display text-sm font-semibold text-white transition-colors duration-200 hover:border-cad/70"
              >
                + Add another property
                <span className="mt-1 block font-sans text-xs font-normal text-white/50">Each project picks Free, Standard or Premium on its own.</span>
              </button>

              {/* 7. Payments row */}
              <BentoCard data-testid="billing-payments-row" className="flex items-start gap-3 bg-obsidian p-6">
                <Shield size={18} className="mt-0.5 shrink-0 text-cad" />
                <span className="font-sans text-sm leading-relaxed text-white/70">
                  Secure payments by PayFast: card, Instant EFT and other South African methods. We never store your card details.
                </span>
              </BentoCard>

              {/* 8. Payment history */}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24, duration: 0.6, ease: EASE }}>
                <BentoCard data-testid="billing-history" className="flex flex-col bg-obsidian p-6 md:p-8">
                  <div className="flex items-center justify-between">
                    <Eyebrow>Payment history</Eyebrow>
                    {history.length > 3 && (
                      <button type="button" data-testid="billing-view-all" onClick={() => setShowAllHistory((v) => !v)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-safety">
                        {showAllHistory ? "Show less" : "View all"}
                      </button>
                    )}
                  </div>
                  {shownHistory.length === 0 ? (
                    <p className="py-6 font-sans text-sm text-white/40">No payments yet.</p>
                  ) : (
                    <div className="mt-1 flex flex-col divide-y divide-cad/15">
                      {shownHistory.map((c) => <ChargeRow key={c.id} charge={c} />)}
                    </div>
                  )}
                </BentoCard>
              </motion.div>

              {/* 9. Info row */}
              <BentoCard data-testid="billing-why" className="flex flex-col bg-obsidian">
                <button type="button" data-testid="billing-why-toggle" onClick={() => setWhyOpen((v) => !v)} className="flex items-center justify-between px-6 py-5 text-left">
                  <span className="font-sans text-sm text-white">Why am I not charged for revised plans?</span>
                  <motion.span animate={{ rotate: whyOpen ? 180 : 0 }} transition={{ duration: 0.25 }} className="text-cad"><ChevronDown size={16} /></motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {whyOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: EASE }} className="overflow-hidden">
                      <p className="px-6 pb-5 font-sans text-sm leading-relaxed text-white/60">
                        Your plan belongs to this project. Revised plans, extra drawings and amendments for the same property are always included — you're never charged again. A genuinely different property needs its own project with its own plan.
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </BentoCard>

              {/* 10. Footer */}
              <div className="flex flex-col gap-5 pt-2">
                <div className="flex flex-wrap items-center gap-5 font-mono text-[10px] uppercase tracking-[0.16em] text-cad">
                  <button type="button" data-testid="billing-refund-policy" onClick={() => navigate("/legal/terms")} className="hover:text-white">Cooling-off &amp; refunds</button>
                  <button type="button" data-testid="billing-terms" onClick={() => navigate("/legal/terms")} className="hover:text-white">Terms</button>
                </div>
                {ent.tier !== "free" && ent.status === "active" && (
                  <button
                    type="button"
                    data-testid="billing-request-refund"
                    onClick={() => setRefundOpen(true)}
                    className="self-start rounded-xl border border-cad/40 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-cad transition-colors duration-200 hover:border-cad/70 hover:text-white/70"
                  >
                    Request a refund
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Refund flow */}
      <AnimatePresence>
        {refundOpen && project && (
          <>
            <motion.button type="button" aria-label="Close" data-testid="refund-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setRefundOpen(false)} className="fixed inset-0 z-50 bg-obsidian/70 backdrop-blur-sm" />
            <motion.div role="dialog" aria-label="Request a refund" data-testid="refund-dialog" initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.25, ease: EASE }} className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2.5rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-cad/40 bg-obsidian-deep p-6">
              <h3 className="font-display text-xl font-semibold text-white">Request a refund</h3>
              <p className="mt-2 font-sans text-sm leading-relaxed text-white/60">
                We follow our Cooling-off &amp; Refund Policy. Refunds revert this project to the Free plan and remove paid entitlements. Continue?
              </p>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button type="button" data-testid="refund-cancel" onClick={() => setRefundOpen(false)} className="rounded-xl px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-cad hover:text-white">Keep plan</button>
                <button type="button" data-testid="refund-confirm" onClick={() => { requestRefund(project.id, project.name); setRefundOpen(false); }} className="rounded-xl border border-cad/50 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-white/80 hover:border-white/50">Request refund</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
